import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config.js';
import { closeSocketQuietly } from '../_helpers/webSocketCleanup.js';
import {
  SOCKET_SONG_RECOMMENDATION,
  SocketTicketRefusedError,
  createSocketRetry,
  hasSession,
  mintSocketTicket,
} from '../_helpers/socketTicket.js';

const MAX_RECONNECT_ATTEMPTS = 5;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30000;
const POLLING_INTERVAL_MS = 10000;
const RECOMMENDATION_DISPLAY_DURATION_MS = 8000;

const RECOMMENDATION_WS_ENDPOINT_TEMPLATE = '/ws/song-recommendation/{bandId}';
const RECOMMENDATION_HTTP_ENDPOINT_TEMPLATE = '/public/bands/{bandId}/stream/recommendation';

function getSafeBandId(bandId) {
  const parsed = parseInt(bandId, 10);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function getWsBaseUrl() {
  const apiUrl = config.apiBaseUrl || '';
  return apiUrl.replace(/^https/, 'wss').replace(/^http/, 'ws');
}

// A signed-in viewer's socket carries a single-use ticket minted just before
// the connect (ENG-498), never the session JWT; a signed-out viewer's none.
function getRecommendationWsUrl(safeBandId, ticket) {
  const ticketParam = ticket ? `?ticket=${encodeURIComponent(ticket)}` : '';
  return `${getWsBaseUrl()}${RECOMMENDATION_WS_ENDPOINT_TEMPLATE.replace('{bandId}', String(safeBandId))}${ticketParam}`;
}

function getRecommendationHttpUrl(safeBandId) {
  return `${config.apiBaseUrl}${RECOMMENDATION_HTTP_ENDPOINT_TEMPLATE.replace('{bandId}', String(safeBandId))}`;
}

const MAX_STRING_LENGTH = 500;

function truncate(str) {
  return str.length > MAX_STRING_LENGTH ? str.slice(0, MAX_STRING_LENGTH) : str;
}

function normalizeRecommendation(input) {
  if (!input || typeof input !== 'object') {
    return null;
  }
  const title = truncate(String(input.title ?? input.Title ?? '').trim());
  const artist = truncate(String(input.artist ?? input.Artist ?? '').trim());
  if (!title) {
    return null;
  }
  const requestedByRaw = String(input.requestedBy ?? input.requested_by ?? '').trim();
  return {
    title,
    artist,
    requestedBy: requestedByRaw ? truncate(requestedByRaw) : null,
  };
}

export const useSongRecommendation = ({ bandId }) => {
  const [recommendation, setRecommendation] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting');

  const stateRef = useRef({
    ws: null,
    pollingTimer: null,
    reconnectTimer: null,
    reconnectAttempts: 0,
    dismissTimer: null,
    mounted: true,
  });

  const dismissRecommendation = useCallback(() => {
    const state = stateRef.current;
    if (state.dismissTimer) {
      clearTimeout(state.dismissTimer);
      state.dismissTimer = null;
    }
    setRecommendation(null);
  }, []);

  const showRecommendation = useCallback((normalized) => {
    const state = stateRef.current;
    if (state.dismissTimer) {
      clearTimeout(state.dismissTimer);
    }
    setRecommendation(normalized);
    state.dismissTimer = setTimeout(() => {
      if (!state.mounted) return;
      setRecommendation(null);
      state.dismissTimer = null;
    }, RECOMMENDATION_DISPLAY_DURATION_MS);
  }, []);

  useEffect(() => {
    const state = stateRef.current;
    state.mounted = true;
    state.reconnectAttempts = 0;

    const safeBandId = getSafeBandId(bandId);
    if (!safeBandId) {
      setConnectionStatus('disconnected');
      return () => {
        state.mounted = false;
      };
    }

    const clearPolling = () => {
      if (state.pollingTimer) {
        clearInterval(state.pollingTimer);
        state.pollingTimer = null;
      }
    };

    const clearReconnect = () => {
      if (state.reconnectTimer) {
        clearTimeout(state.reconnectTimer);
        state.reconnectTimer = null;
      }
    };

    const pollRecommendation = async () => {
      if (!state.mounted) return;
      try {
        const token = sessionStorage.getItem('jwt');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await fetch(getRecommendationHttpUrl(safeBandId), { headers });
        if (response.status === 401 || response.status === 403) {
          clearPolling();
          return;
        }
        if (!response.ok) return;
        const data = await response.json();
        if (!state.mounted) return;
        const normalized = normalizeRecommendation(data);
        if (normalized) {
          showRecommendation(normalized);
        }
      } catch (_error) {
        // Silently ignore polling errors
      }
    };

    // Falling back to polling, with socket retries (DASH-19), and whether
    // the ticket endpoint refused this session (connect anonymously then).
    let polling = false;
    let anonymous = false;
    const socketRetry = createSocketRetry(() => connect());

    const startPolling = () => {
      if (!state.mounted) return;
      polling = true;
      setConnectionStatus('polling');
      clearPolling();
      pollRecommendation();
      state.pollingTimer = setInterval(pollRecommendation, POLLING_INTERVAL_MS);
      socketRetry.schedule();
    };

    // A socket that closed, or a connect that couldn't get a ticket.
    const handleSocketLost = () => {
      if (!state.mounted) return;
      state.ws = null;

      if (polling) {
        socketRetry.schedule();
        return;
      }

      if (state.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        startPolling();
        return;
      }

      const delay = Math.min(
        INITIAL_RECONNECT_DELAY_MS * Math.pow(2, state.reconnectAttempts),
        MAX_RECONNECT_DELAY_MS
      );
      state.reconnectAttempts += 1;
      setConnectionStatus('reconnecting');
      clearReconnect();
      state.reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (!state.mounted) return;
      if (!polling) setConnectionStatus(state.reconnectAttempts > 0 ? 'reconnecting' : 'connecting');
      if (anonymous || !hasSession()) {
        openSocket(null);
        return;
      }
      mintSocketTicket(SOCKET_SONG_RECOMMENDATION, safeBandId).then(
        (ticket) => openSocket(ticket),
        (err) => {
          if (!state.mounted) return;
          if (err instanceof SocketTicketRefusedError) {
            anonymous = true;
            openSocket(null);
            return;
          }
          handleSocketLost();
        }
      );
    };

    const openSocket = (ticket) => {
      if (!state.mounted) return;

      try {
        const ws = new WebSocket(getRecommendationWsUrl(safeBandId, ticket));
        state.ws = ws;

        ws.onopen = () => {
          clearPolling();
          polling = false;
          socketRetry.reset();
          setConnectionStatus('connected');
          state.reconnectAttempts = 0;
        };

        ws.onmessage = (event) => {
          if (!state.mounted) return;
          try {
            const payload = JSON.parse(event.data);
            if (payload.type === 'song_recommendation') {
              const normalized = normalizeRecommendation(payload);
              if (normalized) {
                showRecommendation(normalized);
              }
            }
          } catch (_error) {
            // Ignore malformed realtime events.
          }
        };

        ws.onerror = () => {
          // handled by onclose
        };

        ws.onclose = handleSocketLost;
      } catch (_error) {
        startPolling();
      }
    };

    // Open the socket on a zero-delay timer rather than synchronously so
    // StrictMode's dev-only mount → cleanup → mount pass cancels it below
    // before a socket exists; closeSocketQuietly covers a real unmount that
    // lands inside the handshake window.
    state.reconnectTimer = setTimeout(connect, 0);

    return () => {
      state.mounted = false;
      clearReconnect();
      clearPolling();
      socketRetry.cancel();
      if (state.dismissTimer) {
        clearTimeout(state.dismissTimer);
        state.dismissTimer = null;
      }
      closeSocketQuietly(state.ws);
      state.ws = null;
    };
  }, [bandId, showRecommendation]);

  return { recommendation, dismissRecommendation, connectionStatus };
};

export { normalizeRecommendation };
